import { ReactElement, useState, useEffect } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { color, font, space } from '@op/opds.tokens';
import { Col, List, ListItem, Row, Tab, Tabs, Tile } from '@op/opds.opux';
import { Message, useMessageGetter } from '@messageformat/react';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import CourseTaskHeader from '../../CourseTaskHeader';
import { ReactComponent as Circle } from '../../../images/circle.svg';
import { ReactComponent as RectangleTilted } from '../../../images/rectangle-tilted.svg';
import { formatNumber } from '../../../utils';
import getEtfValueAndCosts, {
  Fees as EtfFees,
  Investment as EtfInvestment,
  ReturnValues as EtfReturnValues,
} from './getEtfValueAndCosts';
import getStockValueAndCosts, {
  Fees as StockFees,
  Investment as StockInvestment,
  ReturnValues as StockReturnValues,
} from './getStockValueAndCosts';
import getFundValueAndCosts, {
  Fees as FundFees,
  Investment as FundInvestment,
  ReturnValues as FundReturnValues,
} from './getFundValueAndCosts';
import FundsTab from './FundsTab';
import StocksTab from './StocksTab';
import EtfsTab from './EtfsTab';
import { getUrlEncodedCircle, getUrlEncodedDiamond } from '../../../util/svgIcons';
import { getChartOptionsWithOverrides } from '../../../util/defaultOptions';

const MONTHS_IN_YEAR = 12;
const defaultFundValue = 18452.01869448154;
const defaultStockValue = 3275.489928896737;
const defaultEtfValue = 18525.796858139336;

const TaskOne = (): ReactElement => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [totalInvestmentValue, setTotalInvestmentValue] = useState(0);
  const [totalCostsValue, setTotalCostsValue] = useState(0);
  const [profitData, setProfitData] = useState<
    { y: number; marker: { enabled: boolean } }[]
  >([]);
  const [costsData, setCostsData] = useState<
    { y: number; marker: { enabled: boolean } }[]
  >([]);

  const [fundInitialInvestment, setFundInitialInvestment] = useState(2000);
  const [fundMonthlyInvestment, setFundMonthlyInvestment] = useState(100);
  const [fundInvestmentPeriod, setFundInvestmentPeriod] = useState(10);
  const [fundAnnualReturn, setFundAnnualReturn] = useState(5);
  const [fundMarkingFee, setFundMarkingFee] = useState(0);
  const [fundGovernanceFee, setFundGovernanceFee] = useState(0.3);
  const [fundRedemptionFee, setFundRedemptionFee] = useState(0);

  const [stockInvestment, setStockInvestment] = useState(2000);
  const [stockInvestmentPeriod, setStockInvestmentPeriod] = useState(10);
  const [stockAnnualReturn, setStockAnnualReturn] = useState(5);
  const [stockTradingCommission, setStockTradingCommission] = useState(0.17);
  const [stockMinimumFee, setStockMinimumFee] = useState(7);
  const [stockPriceCeiling, setStockPriceCeiling] = useState(1);
  const [stockCurrencyChangeExpense, setStockCurrencyChangeExpense] = useState(0);

  const [etfInitialInvestment, setEtfInitialInvestment] = useState(2000);
  const [etfMonthlyInvestment, setEtfMonthlyInvestment] = useState(100);
  const [etfInvestmentPeriod, setEtfInvestmentPeriod] = useState(10);
  const [etfAnnualReturn, setEtfAnnualReturn] = useState(5);
  const [etfTradingCommission, setEtfTradingCommission] = useState(0.17);
  const [etfMinimumFee, setEtfMinimumFee] = useState(7);
  const [etfPriceCeiling, setEtfPriceCeiling] = useState(1);
  const [etfCurrencyChangeExpense, setEtfCurrencyChangeExpense] = useState(0);
  const [etfRunningCosts, setEtfRunningCosts] = useState(0.3);
  const [etfOperatingCost, setEtfOperatingCost] = useState(0);
  const [resetPressedTab1, setResetPressedTab1] = useState<boolean>(false);
  const [resetPressedTab2, setResetPressedTab2] = useState<boolean>(false);
  const [resetPressedTab3, setResetPressedTab3] = useState<boolean>(false);

  const getText = useMessageGetter('course6.task1');

  useEffect(() => {
    if (selectedTab === 0) {
      const fees: FundFees = {
        markingFee: fundMarkingFee,
        governanceFee: fundGovernanceFee,
        redemptionFee: fundRedemptionFee,
      };
      const investment: FundInvestment = {
        initialInvestment: fundInitialInvestment,
        monthlyInvestment: fundMonthlyInvestment,
        investmentPeriod: fundInvestmentPeriod,
        annualReturn: fundAnnualReturn,
      };

      const result: FundReturnValues = getFundValueAndCosts(fees, investment);
      setCostsData(result.costsData);
      setTotalInvestmentValue(result.value);
      if (resetPressedTab1 === true && defaultFundValue !== result.value)
        setResetPressedTab1(false);
      setTotalCostsValue(result.cost);
      setProfitData(result.profitData);
    } else if (selectedTab === 1) {
      const fees: StockFees = {
        tradingFee: stockTradingCommission,
        maximumFeePercentage: stockPriceCeiling,
        minimumFee: stockMinimumFee,
        currencyExchangeFee: stockCurrencyChangeExpense,
      };
      const investment: StockInvestment = {
        initialInvestment: stockInvestment,
        investmentPeriod: stockInvestmentPeriod,
        annualReturn: stockAnnualReturn,
      };

      const result: StockReturnValues = getStockValueAndCosts(fees, investment);
      setCostsData(result.costsData);
      setTotalInvestmentValue(result.value);
      if (resetPressedTab2 === true && defaultStockValue !== result.value)
        setResetPressedTab2(false);
      setTotalCostsValue(result.cost);
      setProfitData(result.profitData);
    } else if (selectedTab === 2) {
      const fees: EtfFees = {
        tradingFee: etfTradingCommission,
        maximumFeePercentage: etfPriceCeiling,
        minimumFee: etfMinimumFee,
        ongoingCharge: etfRunningCosts,
        currencyExchangeExpense: etfCurrencyChangeExpense,
        operatingExpense: etfOperatingCost,
      };
      const investment: EtfInvestment = {
        investment: etfInitialInvestment,
        monthlyInvestment: etfMonthlyInvestment,
        years: etfInvestmentPeriod,
        annualReturn: etfAnnualReturn,
      };

      const result: EtfReturnValues = getEtfValueAndCosts(fees, investment);
      setCostsData(result.costsData);
      setTotalInvestmentValue(result.value);
      if (resetPressedTab3 === true && defaultEtfValue !== result.value)
        setResetPressedTab3(false);
      setTotalCostsValue(result.cost);
      setProfitData(result.profitData);
    }
  }, [
    selectedTab,
    fundInitialInvestment,
    fundMonthlyInvestment,
    fundInvestmentPeriod,
    fundAnnualReturn,
    fundMarkingFee,
    fundGovernanceFee,
    fundRedemptionFee,
    stockInvestment,
    stockInvestmentPeriod,
    stockAnnualReturn,
    stockTradingCommission,
    stockMinimumFee,
    stockPriceCeiling,
    stockCurrencyChangeExpense,
    etfInitialInvestment,
    etfMonthlyInvestment,
    etfInvestmentPeriod,
    etfAnnualReturn,
    etfTradingCommission,
    etfMinimumFee,
    etfPriceCeiling,
    etfCurrencyChangeExpense,
    etfRunningCosts,
    etfOperatingCost,
    resetPressedTab1,
    resetPressedTab2,
    resetPressedTab3,
  ]);

  const options = getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    chart: {
      marginTop: 20,
      zooming: {
        mouseWheel: false,
      },
    },
    credits: { enabled: false },
    navigator: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        enableMouseTracking: false,
        marker: {
          enabled: false,
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      showLastLabel: true,
      showFirstLabel: false,
      opposite: true,
      labels: {
        format: '{value:,.0f} €',
        reserveSpace: true,
        distance: 10,
      },
      tickAmount: 8,
    },
    xAxis: {
      tickPositioner(this: Highcharts.Axis): number[] {
        const positions: number[] = [];
        // eslint-disable-next-line react/no-this-in-sfc
        const maxYears = Math.floor(((this.max ?? 0) + 1) / MONTHS_IN_YEAR);
        const tickInterval = Math.floor(maxYears / 5);
        if (maxYears <= 5) {
          for (let i = 0; i <= maxYears; i += 1) {
            positions.push(i * MONTHS_IN_YEAR);
          }
        } else {
          for (let i = 0; i <= tickInterval; i += 1) {
            positions.push(i * 5 * MONTHS_IN_YEAR);
          }
          // Ensure the max year value is included, so that endOnTick doesn't stretch the graph too much
          if (maxYears * MONTHS_IN_YEAR > positions[positions.length - 1]) {
            positions.push(maxYears * MONTHS_IN_YEAR);
          }
        }
        return positions;
      },
      endOnTick: true,
      labels: {
        formatter: (ctx: Highcharts.AxisLabelsFormatterContextObject) => {
          if (typeof ctx.value === 'number') {
            return `${ctx.value / MONTHS_IN_YEAR} ${getText('abbr.year')}`;
          }
          return '';
        },
      },
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        type: 'line',
        color: color.indicator.positive,
        data: profitData,
        marker: {
          symbol: getUrlEncodedCircle(color.indicator.positive),
        },
      },
      {
        type: 'line',
        color: color.indicator.negative,
        data: costsData,
        marker: {
          symbol: getUrlEncodedDiamond(color.indicator.negative),
        },
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
  });

  const resetValues = (): void => {
    if (selectedTab === 0) {
      setResetPressedTab1(true);
      setFundInitialInvestment(2000);
      setFundMonthlyInvestment(100);
      setFundInvestmentPeriod(10);
      setFundAnnualReturn(5);
      setFundMarkingFee(0);
      setFundGovernanceFee(0.3);
      setFundRedemptionFee(0);
    } else if (selectedTab === 1) {
      setResetPressedTab2(true);
      setStockInvestment(2000);
      setStockInvestmentPeriod(10);
      setStockAnnualReturn(5);
      setStockTradingCommission(0.17);
      setStockMinimumFee(7);
      setStockPriceCeiling(1);
      setStockCurrencyChangeExpense(0);
    } else if (selectedTab === 2) {
      setResetPressedTab3(true);
      setEtfInitialInvestment(2000);
      setEtfMonthlyInvestment(100);
      setEtfInvestmentPeriod(10);
      setEtfAnnualReturn(5);
      setEtfTradingCommission(0.17);
      setEtfMinimumFee(7);
      setEtfPriceCeiling(1);
      setEtfCurrencyChangeExpense(0);
      setEtfRunningCosts(0.3);
      setEtfOperatingCost(0);
    }
  };

  return (
    <div style={{ marginTop: space.xxl }}>
      <CourseTaskHeader questionNumber={1} totalQuestions={2} />
      <h3
        className="opds-styles-v1-text-hero-small"
        style={{ textAlign: 'center', marginTop: space.xxl }}
      >
        <Message id="course6.task1.title" />
      </h3>
      <div
        style={{
          marginTop: space.small,
          alignItems: 'center',
          justifyContent: 'center',
          display: 'block',
        }}
      >
        <Tabs
          onChange={(_, index) => {
            setSelectedTab(index);
          }}
          small
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          className="course-6-expenses-calculator"
        >
          <Tab label={getText('tab1.label')}>
            <FundsTab
              fundInitialInvestment={fundInitialInvestment}
              fundInvestmentPeriod={fundInvestmentPeriod}
              fundMonthlyInvestment={fundMonthlyInvestment}
              fundAnnualReturn={fundAnnualReturn}
              fundMarkingFee={fundMarkingFee}
              fundGovernanceFee={fundGovernanceFee}
              fundRedemptionFee={fundRedemptionFee}
              setFundInitialInvestment={setFundInitialInvestment}
              setFundInvestmentPeriod={setFundInvestmentPeriod}
              setFundMonthlyInvestment={setFundMonthlyInvestment}
              setFundAnnualReturn={setFundAnnualReturn}
              setFundMarkingFee={setFundMarkingFee}
              setFundGovernanceFee={setFundGovernanceFee}
              setFundRedemptionFee={setFundRedemptionFee}
              resetValues={resetValues}
              resetPressed={resetPressedTab1}
            />
          </Tab>
          <Tab label={getText('tab2.label')}>
            <StocksTab
              stockInvestment={stockInvestment}
              stockInvestmentPeriod={stockInvestmentPeriod}
              stockAnnualReturn={stockAnnualReturn}
              stockTradingCommission={stockTradingCommission}
              stockMinimumFee={stockMinimumFee}
              stockPriceCeiling={stockPriceCeiling}
              stockCurrencyChangeExpense={stockCurrencyChangeExpense}
              setStockInvestment={setStockInvestment}
              setStockInvestmentPeriod={setStockInvestmentPeriod}
              setStockAnnualReturn={setStockAnnualReturn}
              setStockTradingCommission={setStockTradingCommission}
              setStockMinimumFee={setStockMinimumFee}
              setStockPriceCeiling={setStockPriceCeiling}
              setStockCurrencyChangeExpense={setStockCurrencyChangeExpense}
              resetValues={resetValues}
              resetPressed={resetPressedTab2}
            />
          </Tab>
          <Tab label={getText('tab3.label')}>
            <EtfsTab
              etfInitialInvestment={etfInitialInvestment}
              etfMonthlyInvestment={etfMonthlyInvestment}
              etfInvestmentPeriod={etfInvestmentPeriod}
              etfAnnualReturn={etfAnnualReturn}
              etfTradingCommission={etfTradingCommission}
              etfMinimumFee={etfMinimumFee}
              etfPriceCeiling={etfPriceCeiling}
              etfCurrencyChangeExpense={etfCurrencyChangeExpense}
              etfRunningCosts={etfRunningCosts}
              etfOperatingCost={etfOperatingCost}
              setEtfInitialInvestment={setEtfInitialInvestment}
              setEtfMonthlyInvestment={setEtfMonthlyInvestment}
              setEtfInvestmentPeriod={setEtfInvestmentPeriod}
              setEtfAnnualReturn={setEtfAnnualReturn}
              setEtfTradingCommission={setEtfTradingCommission}
              setEtfMinimumFee={setEtfMinimumFee}
              setEtfPriceCeiling={setEtfPriceCeiling}
              setEtfCurrencyChangeExpense={setEtfCurrencyChangeExpense}
              setEtfRunningCosts={setEtfRunningCosts}
              setEtfOperatingCost={setEtfOperatingCost}
              resetValues={resetValues}
              resetPressed={resetPressedTab3}
            />
          </Tab>
        </Tabs>
      </div>
      <div style={{ display: 'none' }} id="task_one_graph">
        <Message id="course6.task1.task_one_graph_text" />
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        constructorType="stockChart"
        containerProps={{
          style: { height: '40rem', marginTop: space.large },
          'aria-labelledby': 'task_one_graph',
        }}
      />
      <div style={{ textAlign: 'center' }} aria-hidden="true">
        <List
          border="none"
          size="tiny"
          indented
          style={{
            display: 'inline-block',
            textAlign: 'left',
            marginTop: 0,
          }}
        >
          <ListItem style={{ display: 'inline' }}>
            <div className="investoracademy-listitem-container">
              <Circle aria-hidden="true" fill={color.palette.green[500]} />
              <Message id="course6.task1.profit_label" />
            </div>
          </ListItem>
          <ListItem style={{ display: 'inline' }}>
            <div className="investoracademy-listitem-container">
              <RectangleTilted aria-hidden="true" fill={color.palette.red[700]} />
              <Message id="course6.task1.costs_label" />
            </div>
          </ListItem>
        </List>
      </div>
      <Tile>
        <Row>
          <Col
            className="investoracademy-total-value-col investoracademy-total-value-cost-col"
            xs={12}
            md={6}
          >
            <div style={{ textAlign: 'right' }}>
              <p
                style={{
                  fontWeight: font.weight.bold,
                  fontSize: font.size.body.small,
                  padding: 0,
                }}
              >
                <Message id="course6.task1.result_investment_value_title" />
              </p>
              <p
                className="investoracademy-total-value-body-text"
                style={{
                  padding: 0,
                }}
              >
                <Message id="course6.task1.result_investment_value_body" />
              </p>
            </div>
            <p
              data-testid="total-investment-value"
              className="investoracademy-total-value-text"
              style={{
                padding: 0,
                color: color.text.positive.small,
              }}
            >
              {formatNumber(totalInvestmentValue)} €
            </p>
          </Col>
          <Col className="investoracademy-total-value-cost-col" xs={12} md={6}>
            <div style={{ textAlign: 'right' }}>
              <p
                style={{
                  fontWeight: font.weight.bold,
                  fontSize: font.size.body.small,
                  padding: 0,
                }}
              >
                <Message id="course6.task1.result_costs_value_title" />
              </p>
              <p
                className="investoracademy-total-value-body-text"
                style={{
                  padding: 0,
                }}
              >
                <Message id="course6.task1.result_costs_value_body" />
              </p>
            </div>
            <p
              data-testid="total-costs-value"
              className="investoracademy-total-value-text"
              style={{
                padding: 0,
                color: color.text.negative.small,
              }}
            >
              {formatNumber(totalCostsValue)} €
            </p>
          </Col>
        </Row>
      </Tile>
      <p
        className="opds-styles-v1-text-label-small"
        style={{ display: 'flex', paddingBottom: space.default }}
      >
        <IconInformation />
        <Message id="course6.task1.info" />
      </p>
    </div>
  );
};

export default TaskOne;
